/* eslint-disable react/no-multi-comp */
import React from 'react';
import styled from 'styled-components';
import { FormattedMessage, useIntl } from 'react-intl';
import { observer } from 'mobx-react-lite';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';

import {
    CalculatorStepPanel,
    Sizes,
    Question,
    useForm,
    getEmptyNumberPlaceholder,
    DefaultIntlFormatValues,
    MessageBanner,
    MessageBannerCTAVariants,
    MessageBannerVariants,
    HiddenField,
    FormComputed,
    BOOLEAN_OPTIONS,
} from '@ratehub/base-ui';
import {
    MORTGAGE_PLACEHOLDERS,
} from '@ratehub/mtg-common';

import CalculatorResultsBox from './CalculatorResultsBox';


function ContributionLimitRRSPCalcInputStep({
    messageBannerProps,

    currentYear,
    contributionLimits,
}) {
    const form = useForm();
    const intl = useIntl();

    const isMessageBannerCTAEnabled = !!(messageBannerProps?.title || messageBannerProps?.message);

    return (
        <Container
            stepNumber={1}
            isVisible
        >
            <HiddenField
                name="currentYear"
                value={currentYear}
            />

            <HiddenField
                name="currentYearMaxLimit"
                value={(contributionLimits?.find(({ year }) => year === currentYear))?.contribution}
            />

            <FormComputed
                name="calculatedAnnualRRSPLimit"
                value={calculateAnnualRRSPLimit}
                hidden
            />

            <FormComputed
                name="annualContributionLimitRRSP"
                value={calculateCurrentYearContributionRoom}
                hidden
            />

            <FormComputed
                name="resultWarning"
                value={getResultsWarning}
                hidden
            />

            <h2 className="header rh-text-l weight-regular">
                <FormattedMessage
                    id="calculators.RRSP-Contribution-Room.header"
                    defaultMessage="Calculate your contribution room"
                />
            </h2>

            <hr className="rh-horizontal-rule rh-mb-1_5 rh-width-100p" />

            <Question
                className="question-container"
                field={{
                    name: 'currentYearIncome',
                    type: 'integer',
                }}
                labelMessage={(
                    <FormattedMessage
                        id="calculators.RRSP-Contribution-Room.currentYearIncomeLabel"
                        defaultMessage={'What was your income in {year}'}
                        values={{ year: dayjs().year() - 1 }} //previous year
                    />
                )}
                placeholder={MORTGAGE_PLACEHOLDERS.AMOUNT_PLACEHOLDER}
                quicktipMessage={(
                    <FormattedMessage
                        id="calculators.RRSP-Contribution-Room.currentYearIncomeQuicktip"
                        defaultMessage="Total employment income (inlcuding salary, commisions, etc.)"
                    />
                )}
                type="currency"
                isSideBySide={true}
                showOptionalText={false}
            />

            <Question
                className="question-container"
                field={{
                    name: 'annualRRSPLimit',
                    type: 'integer',
                    value: form.values.calculatedAnnualRRSPLimit,
                    required: false,
                }}
                labelMessage={(
                    <FormattedMessage
                        id="calculators.RRSP-Contribution-Room.annualRRSPLimitLabel"
                        defaultMessage="Annual RRSP Limit"
                    />
                )}
                placeholder={getEmptyNumberPlaceholder('CAD0', intl.locale)}
                tooltipMessage={(
                    <FormattedMessage
                        id="calculators.RRSP-Contribution-Room.annualRRSPLimitTooltip"
                        defaultMessage="RRSP Contribution Limit for the calendar year (maxes at 18% of your income)."
                    />
                )}
                type="currency"
                isSideBySide={true}
                showOptionalText={false}
                readOnly={true}
                inputClassName="readOnlyInput rh-title-xs"
            />

            <Question
                className="question-container"
                field={{
                    name: 'totalUnusedContributionRoom',
                    type: 'integer',
                    required: false,
                }}
                labelMessage={(
                    <FormattedMessage
                        id="calculators.RRSP-Contribution-Room.totalUnusedContributionRoomLabel"
                        defaultMessage="What’s your total unused contribution room from previous years?"
                    />
                )}
                placeholder={MORTGAGE_PLACEHOLDERS.AMOUNT_PLACEHOLDER}
                tooltipMessage={(
                    <FormattedMessage
                        id="calculators.RRSP-Contribution-Room.totalUnusedContributionRoomTooltip"
                        defaultMessage="If you don't make the maximum allowable RRSP contribution in any given year,
                                        you can carry forward the unused contribution room."
                    />
                )}
                type="currency"
                isSideBySide={true}
                showOptionalText={false}
            />

            <Question
                className="question-container"
                field={{
                    name: 'hasRegisteredPension',
                    type: 'boolean',
                    required: false,
                    options: BOOLEAN_OPTIONS,
                }}
                type="radio"
                labelMessage={(
                    <FormattedMessage
                        id="calculators.RRSP-Contribution-Room.RegisteredPensionLabel"
                        defaultMessage="Do you currently belong to a registered pension (or have in the past)?"
                    />
                )}
                flowDirection="row"
                isSideBySide={true}
                quicktipMessage={(
                    <FormattedMessage
                        id="calculators.RRSP-Contribution-Room.RegisteredPensionQuicktip"
                        defaultMessage="Includes employer-sponsored pension plans or defined profit sharing plan."
                    />
                )}
            />

            <Question
                className="question-container"
                field={{
                    name: 'pensionAdjustment',
                    type: 'integer',
                    required: false,
                }}
                labelMessage={(
                    <FormattedMessage
                        id="calculators.RRSP-Contribution-Room.pensionAdjustmentLabel"
                        defaultMessage="Pension adjustment (PA)"
                    />
                )}
                placeholder={MORTGAGE_PLACEHOLDERS.AMOUNT_PLACEHOLDER}
                quicktipMessage={(
                    <FormattedMessage
                        id="calculators.RRSP-Contribution-Room.pensionAdjustmentQuicktip"
                        defaultMessage="Value of benefits earned from your pension (box 52 of your T4)."
                    />
                )}
                type="currency"
                isSideBySide={true}
                isVisible={!!form.values.hasRegisteredPension}
                showOptionalText={false}
            />

            <Question
                className="question-container"
                field={{
                    name: 'pensionAdjustmentReversal',
                    type: 'integer',
                    required: false,
                }}
                labelMessage={(
                    <FormattedMessage
                        id="calculators.RRSP-Contribution-Room.pensionAdjustmentReversalLabel"
                        defaultMessage="Pension adjustment reversal (PAR)"
                    />
                )}
                placeholder={MORTGAGE_PLACEHOLDERS.AMOUNT_PLACEHOLDER}
                quicktipMessage={(
                    <FormattedMessage
                        id="calculators.RRSP-Contribution-Room.pensionAdjustmentReversalQuicktip"
                        defaultMessage="Regained RRSP room after leaving a pension (typically reported on T10)."
                    />
                )}
                type="currency"
                isSideBySide={true}
                isVisible={!!form.values.hasRegisteredPension}
                showOptionalText={false}
            />

            <Question
                className="question-container"
                field={{
                    name: 'pastServicePensionAdjustment',
                    type: 'integer',
                    required: false,
                }}
                labelMessage={(
                    <FormattedMessage
                        id="calculators.RRSP-Contribution-Room.pastServicePensionAdjustmentLabel"
                        defaultMessage="Past service pension adjustment (PSPA)"
                    />
                )}
                placeholder={MORTGAGE_PLACEHOLDERS.AMOUNT_PLACEHOLDER}
                quicktipMessage={(
                    <FormattedMessage
                        id="calculators.RRSP-Contribution-Room.pastServicePensionAdjustmentQuicktip"
                        defaultMessage="Recalculation of previous years' Pension Adjustment (typically reported on T215)."
                    />
                )}
                type="currency"
                isSideBySide={true}
                isVisible={!!form.values.hasRegisteredPension}
                showOptionalText={false}
            />

            <CalculatorResultsBox
                className="box"
                resultMessage={(
                    <FormattedMessage
                        id="calculators.RRSP-Contribution-Room.resultMessage"
                        defaultMessage={'Your contribution room for <b>{year}</b>'}
                        values={{
                            year: dayjs().year(),
                            ...DefaultIntlFormatValues,
                        }}
                    />
                )}
                resultValue={form.values.annualContributionLimitRRSP >= 0 ? form.values.annualContributionLimitRRSP : undefined}
                resultWarningMessage={form.values.resultWarning}
            />

            <If condition={isMessageBannerCTAEnabled}>
                <MessageBanner
                    className="messageBanner"
                    variant={MessageBannerVariants.NOTIFICATION}

                    {...messageBannerProps}
                    messageSize="small"

                    ctaDataName="contribution-limit-rrsp-learnMore"
                    ctaVariant={MessageBannerCTAVariants.PRIMARY_ANCHOR}
                />
            </If>
        </Container>
    );
}

ContributionLimitRRSPCalcInputStep.propTypes = {
    messageBannerProps: PropTypes.shape({
        title: PropTypes.string,
        message: PropTypes.string,
        ctaMessage: PropTypes.string,
        href: PropTypes.string,
    }),

    currentYear: PropTypes.number,
    contributionLimits: PropTypes.arrayOf(PropTypes.shape({
        year: PropTypes.number,
        contribution: PropTypes.number,
    })),
};

ContributionLimitRRSPCalcInputStep.defaultProps = {
    messageBannerProps: undefined,
    currentYear: undefined,
    contributionLimits: undefined,
};

function calculateAnnualRRSPLimit({ currentYearIncome, currentYearMaxLimit }) {
    if (currentYearIncome == undefined || currentYearMaxLimit == undefined) {
        return null;
    }

    //RRSP annual limit is their current income multiplied by 0.18 up to a max of the current year max limit
    return Math.min(currentYearIncome*0.18, currentYearMaxLimit);
}

function calculateCurrentYearContributionRoom({
    calculatedAnnualRRSPLimit,
    totalUnusedContributionRoom,
    pensionAdjustment,
    pensionAdjustmentReversal,
    pastServicePensionAdjustment,
}) {
    if (calculatedAnnualRRSPLimit == undefined) {
        return undefined;
    }

    const pensionAdjustmentValue = pensionAdjustment ?? 0;
    const pensionAdjustmentReversalValue = pensionAdjustmentReversal ?? 0;
    const pastServicePensionAdjustmentValue = pastServicePensionAdjustment ?? 0;

    return (calculatedAnnualRRSPLimit + totalUnusedContributionRoom - pensionAdjustmentValue
            + pensionAdjustmentReversalValue - pastServicePensionAdjustmentValue);
}

// The linter thinks this is a react component because we are returning formattedMessage
// eslint-disable-next-line react/prop-types
function getResultsWarning({ annualContributionLimitRRSP }) {
    if (annualContributionLimitRRSP >= 0 || annualContributionLimitRRSP == undefined) {
        return undefined;
    }

    return (
        <FormattedMessage
            id="calculators.RRSP-Contribution-Room.resultWarningMessage"
            defaultMessage="You have no available contribution room this year or over contributed."
        />
    );
}

const Container = styled(CalculatorStepPanel)`
    padding: ${Sizes.SPACING.ONE_AND_A_HALF} ${Sizes.SPACING.ONE_AND_A_HALF} 0;

    > .header {
        margin-top: 0;
        margin-bottom: ${Sizes.SPACING.ONE_AND_A_HALF};
    }

    .question-container {
        margin-top: 0;
        margin-bottom: ${Sizes.SPACING.TWO};
    }

    > .box {
        margin-top: 0;
        margin-bottom: ${Sizes.SPACING.ONE_AND_A_HALF};
    }

    .readOnlyInput {
        height: ${Sizes.SPACING.TWO};
        line-height: ${Sizes.SPACING.TWO};

        padding-top: 0.5rem; //align the text with tooltip and question label
    }

    > .messageBanner {
        margin-bottom: ${Sizes.SPACING.TWO};
    }
`;

export default observer(ContributionLimitRRSPCalcInputStep);
